
<template>
  <AlertProvider>
    <AlertBridgeSetup />
    <component :is="layoutComp">
      <router-view :key="route.path + (route.query._t || '')"/>
    </component>
    <!-- 팝업매니저는 레이아웃 밖에서 전체 페이지 기준으로 렌더링 -->
    <PopupManager :popups="popups" />
    <LoadingOverlay />
    <ModalRenderer/>
  </AlertProvider>
</template>


<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { AlertProvider } from '@cnd_fe/alert'
import LoadingOverlay from '@/components/feedback/LoadingOverlay.vue'
import { BlankLayout, DefaultLayout, MainLayout } from "@/layout"
import { useApiMenu } from '@/composables/useApiMenu.js'
import { usePopup } from '@/composables/usePopup.js'
import { useTableScroll } from '@/composables/useTableScroll.js'
import ModalRenderer from './components/common/ModalRenderer.vue'
import { PopupManager } from "@/components/overlay/index.js";
import AlertBridgeSetup from '@/components/common/AlertBridgeSetup.vue'
import { showAlert } from '@/utils/alert/alertBridge'

const route = useRoute()
const domainId = import.meta.env.VITE_DOMAIN_ID

// AS-IS common.js — 테이블 모바일 스크롤 래핑 (전역)
useTableScroll()
const { navMenusRaw, loadAllMenuData } = useApiMenu()
const { popups, fetchPopups } = usePopup()
const isLoadingMenus = ref(false)

/**
 * 현재 라우트와 일치하는 메뉴의 menuId 찾기
 */
const findCurrentMenuId = () => {
  if (!navMenusRaw.value.length) return null

  const currentPath = route.path
  const matches = []

  for (const menu of navMenusRaw.value) {
    // path: 변환된 실제 라우트 경로, menuLink: 원본 링크 — 둘 다 비교
    const link = menu.path || menu.menuLink
    if (link && currentPath.includes(link)) {
      matches.push({ menuId: menu.menuId, linkLen: link.length })
    }
  }

  if (matches.length === 0) return null

  // 가장 구체적인 path(길이가 긴)를 우선 매칭
  matches.sort((a, b) => b.linkLen - a.linkLen)
  return matches[0].menuId
}

/**
 * 현재 상태에 맞는 팝업 조회
 * - menuId가 있으면 해당 메뉴 팝업만
 * - instId가 있으면 기관 기반 역추적
 * - 둘 다 없으면 domainId로 조회 (메인 페이지)
 */
const loadPopups = async () => {
  const menuId = route.query?.menuId || findCurrentMenuId()
  if (menuId) {
    await fetchPopups({ menuId })
  } else if (route.params?.instId) {
    await fetchPopups({ domainId, instId: route.params.instId })
  } else if (route.path === '/') {
    await fetchPopups({ domainId: import.meta.env.VITE_DOMAIN_ID })
  } else {
    popups.value = []
  }
}

// 라우트 변경 시 팝업 조회 — 메뉴 로드 완료 상태에서만 동작
watch(() => route.path, async () => {
  if (navMenusRaw.value.length > 0) {
    await loadPopups()
  }
  // 메뉴 미로드 상태 → 스킵 (onMounted에서 메뉴 로드 후 조회)
}, { immediate: true })

// App 마운트 시 전역 메뉴 로드 (한 번만)
onMounted(async () => {
  if (isLoadingMenus.value || navMenusRaw.value.length || !domainId) return
  isLoadingMenus.value = true
  try {
    await loadAllMenuData(domainId)
    // 메뉴 로드 완료 후 menuId 기반 팝업 조회 (최초 로드 시 스킵된 팝업 보정)
    await loadPopups()
  } catch (error) {
    await showAlert(error?.serverMessage || '메뉴 조회에 실패했습니다')
  } finally {
    isLoadingMenus.value = false
  }
})

// meta.layout 값 읽기 (없으면 'default')
const layoutKey = computed(() => {
  const layout = (route.meta && route.meta.layout) ? route.meta.layout : 'default'
  return layout
})

// 키 → 컴포넌트 매핑
const layoutMap = {
  default: DefaultLayout,
  blank: BlankLayout,
  main: MainLayout,
}

// 실제로 쓸 레이아웃 컴포넌트
const layoutComp = computed(() => layoutMap[layoutKey.value] || DefaultLayout)
</script>

<style>
/* CSS는 main.js에서 이미 import됨 */
</style>